import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from '@/components/px/SectionLabel'
import { createClient, mutationErrorDismissed, updateClient } from '@/features/clients/clientsSlice'
import { validateClientInput } from '@/features/clients/validate'
import type { ApiClientRow, ClientInput } from '@/features/clients/types'
import { useApiErrorMessage } from '@/i18n/useApiErrorMessage'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store'

const inputClass = 'rounded-md border border-input bg-card px-2 py-1.5 text-sm text-foreground'

/** A blank editable payload — the contacts repeater starts with one row. */
const blankInput = (): ClientInput => ({
  name: '',
  status: 'active',
  profile: { industry: '', website: '', notes: '' },
  contacts: [{ name: '', email: '', role: '', phone: '' }],
  commercialTerms: { currency: '', paymentTermsDays: undefined, defaultRate: undefined, notes: '' },
  dataRoomRoot: ''
})

/**
 * The create/edit client dialog (PX-W6.2, AC-5). Validation runs
 * client-side BEFORE submit (`validateClientInput`); server errors map inline
 * (409 `client.duplicate` on the name field).
 *
 * Edit mode seeds from the loaded row and PUTs the COMPLETE editable payload —
 * the server saves the patch wholesale (only blank name/status fall back), so
 * a sparse body would null the untouched nested objects.
 */
export function ClientFormDialog({
  open,
  /** null = create; a row = edit seeded from it. */
  row,
  onCloseClicked
}: {
  open: boolean
  row: ApiClientRow | null
  onCloseClicked: () => void
}) {
  if (!open) return null
  // The BODY mounts fresh per open/mode (keyed), so its form state
  // INITIALISES from the row — no effect-seeded state.
  return (
    <ClientFormDialogBody
      key={row?.id ?? 'new'}
      row={row}
      onCloseClicked={onCloseClicked}
    />
  )
}

function ClientFormDialogBody({
  row,
  onCloseClicked
}: {
  row: ApiClientRow | null
  onCloseClicked: () => void
}) {
  const { t } = useTranslation('clients')
  const toMessage = useApiErrorMessage()
  const dispatch = useAppDispatch()
  const mutationError = useAppSelector((s) => s.clients.mutationError)

  const [input, setInput] = useState<ClientInput>(() =>
    row !== null
      ? {
          name: row.name,
          status: row.status ?? 'active',
          profile: { industry: '', website: '', notes: '', ...(row.profile ?? {}) },
          contacts: (row.contacts ?? []).map((contact) => ({ ...contact })),
          commercialTerms: { ...(row.commercialTerms ?? {}) },
          dataRoomRoot: row.dataRoomRoot ?? ''
        }
      : blankInput()
  )
  const [validation, setValidation] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  const onFieldChanged = (patch: Partial<ClientInput>): void => {
    setInput((current) => ({ ...current, ...patch }))
    setValidation({})
    dispatch(mutationErrorDismissed())
  }

  const onProfileChanged = (patch: Partial<NonNullable<ClientInput['profile']>>): void => {
    setInput((current) => ({ ...current, profile: { ...(current.profile ?? {}), ...patch } }))
  }

  const onTermsChanged = (
    patch: Partial<NonNullable<ClientInput['commercialTerms']>>
  ): void => {
    setInput((current) => ({
      ...current,
      commercialTerms: { ...(current.commercialTerms ?? {}), ...patch }
    }))
  }

  const onContactChanged = (index: number, patch: Record<string, string>): void => {
    setInput((current) => {
      const contacts = (current.contacts ?? []).map((contact, i) =>
        i === index ? { ...contact, ...patch } : contact
      )
      return { ...current, contacts }
    })
    setValidation({})
  }

  const onAddContactClicked = (): void => {
    setInput((current) => ({
      ...current,
      contacts: [...(current.contacts ?? []), { name: '', email: '', role: '', phone: '' }]
    }))
  }

  const onRemoveContactClicked = (index: number): void => {
    setInput((current) => ({
      ...current,
      contacts: (current.contacts ?? []).filter((_, i) => i !== index)
    }))
  }

  const onSubmitClicked = async (): Promise<void> => {
    const errors = validateClientInput(input)
    setValidation(errors)
    if (Object.keys(errors).length > 0) return

    setSubmitting(true)
    try {
      if (row !== null) {
        await dispatch(updateClient({ clientId: row.id, input })).unwrap()
      } else {
        await dispatch(createClient(input)).unwrap()
      }
      onCloseClicked()
    } catch {
      // The slice's mutationError renders inline below.
    } finally {
      setSubmitting(false)
    }
  }

  const duplicate = mutationError?.backendKey === 'client.duplicate'

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={row !== null ? t('form.editTitle') : t('form.createTitle')}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-foreground/30 px-4 py-10"
    >
      <div className="w-full max-w-2xl rounded-lg border border-border bg-card px-5 py-4 shadow-lg">
        <h2 className="font-serif text-xl font-semibold">
          {row !== null ? t('form.editTitle') : t('form.createTitle')}
        </h2>

        <div className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm text-ink-soft">
            {t('form.name')}
            <input
              type="text"
              value={input.name}
              onChange={(event) => onFieldChanged({ name: event.target.value })}
              aria-invalid={validation['name'] !== undefined || duplicate}
              aria-describedby={validation['name'] !== undefined || duplicate ? 'client-name-error' : undefined}
              className={cn(inputClass, 'w-full')}
            />
          </label>
          {row === null && (
            <label className="flex flex-col gap-1 text-sm text-ink-soft">
              {t('form.slug')}
              <input
                type="text"
                value={input.id ?? ''}
                onChange={(event) => onFieldChanged({ id: event.target.value })}
                className={cn(inputClass, 'w-full')}
                aria-describedby="client-slug-hint"
              />
              <span id="client-slug-hint" className="text-xs text-muted-foreground">
                {t('form.slugHint')}
              </span>
            </label>
          )}
          <label className="flex flex-col gap-1 text-sm text-ink-soft">
            {t('form.status')}
            <select
              value={input.status ?? 'active'}
              onChange={(event) => onFieldChanged({ status: event.target.value })}
              className={cn(inputClass, 'w-full')}
            >
              <option value="active">{t('status.active')}</option>
              <option value="archived">{t('status.archived')}</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm text-ink-soft">
            {t('form.dataRoomRoot')}
            <input
              type="text"
              value={input.dataRoomRoot ?? ''}
              onChange={(event) => onFieldChanged({ dataRoomRoot: event.target.value })}
              className={cn(inputClass, 'w-full font-mono text-xs')}
            />
          </label>
        </div>

        {(validation['name'] !== undefined || duplicate) && (
          <p id="client-name-error" role="alert" className="mt-2 text-sm text-blocked">
            {duplicate ? toMessage(mutationError) : t(validation['name'] ?? '')}
          </p>
        )}

        <div className="mt-5">
          <SectionLabel>{t('form.profileSection')}</SectionLabel>
          <div className="mt-2 grid gap-x-6 gap-y-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm text-ink-soft">
              {t('form.industry')}
              <input
                type="text"
                value={input.profile?.industry ?? ''}
                onChange={(event) => onProfileChanged({ industry: event.target.value })}
                className={cn(inputClass, 'w-full')}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-ink-soft">
              {t('form.website')}
              <input
                type="text"
                value={input.profile?.website ?? ''}
                onChange={(event) => onProfileChanged({ website: event.target.value })}
                className={cn(inputClass, 'w-full')}
              />
            </label>
          </div>
          <label className="mt-3 flex flex-col gap-1 text-sm text-ink-soft">
            {t('form.profileNotes')}
            <textarea
              rows={2}
              value={input.profile?.notes ?? ''}
              onChange={(event) => onProfileChanged({ notes: event.target.value })}
              className={cn(inputClass, 'w-full')}
            />
          </label>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between">
            <SectionLabel>{t('form.contactsSection')}</SectionLabel>
            <button
              type="button"
              onClick={onAddContactClicked}
              className="rounded-md border border-input px-2 py-1 text-xs text-ink-soft hover:bg-muted"
            >
              {t('form.addContact')}
            </button>
          </div>
          <div className="mt-2 space-y-2">
            {(input.contacts ?? []).map((contact, index) => (
              <div
              key={index}
              className="grid gap-2 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_0.7fr_0.7fr_auto]"
            >
                <input
                  type="text"
                  value={contact.name ?? ''}
                  onChange={(event) => onContactChanged(index, { name: event.target.value })}
                  placeholder={t('form.contactName')}
                  aria-label={t('form.contactName')}
                  className={`${inputClass} w-full`}
              />
                <input
                  type="text"
                  value={contact.email ?? ''}
                  onChange={(event) => onContactChanged(index, { email: event.target.value })}
                  placeholder={t('form.contactEmail')}
                  aria-label={t('form.contactEmail')}
                  aria-invalid={validation[`contacts.${index}.email`] !== undefined}
                  className={`${inputClass} w-full`}
              />
                <input
                  type="text"
                  value={contact.role ?? ''}
                  onChange={(event) => onContactChanged(index, { role: event.target.value })}
                  placeholder={t('form.contactRole')}
                  aria-label={t('form.contactRole')}
                  className={`${inputClass} w-full`}
              />
                <input
                  type="text"
                  value={contact.phone ?? ''}
                  onChange={(event) => onContactChanged(index, { phone: event.target.value })}
                  placeholder={t('form.contactPhone')}
                  aria-label={t('form.contactPhone')}
                  className={`${inputClass} w-full`}
              />
                <button
                  type="button"
                  onClick={() => onRemoveContactClicked(index)}
                  aria-label={t('form.removeContact')}
                  className="rounded-md border border-input px-2 py-1 text-xs text-blocked hover:bg-muted"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          {Object.entries(validation)
            .filter(([key]) => key.startsWith('contacts.'))
            .map(([key]) => (
              <p key={key} role="alert" className="mt-1 text-sm text-blocked">
                {t(validation[key] ?? '')}
              </p>
            ))}
        </div>

        <div className="mt-5">
          <SectionLabel>{t('form.termsSection')}</SectionLabel>
          <div className="mt-2 grid gap-x-6 gap-y-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-sm text-ink-soft">
              {t('form.currency')}
              <input
                type="text"
                value={input.commercialTerms?.currency ?? ''}
                onChange={(event) => onTermsChanged({ currency: event.target.value })}
                className={cn(inputClass, 'w-full')}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-ink-soft">
              {t('form.paymentTermsDays')}
              <input
                type="number"
                min={0}
                value={input.commercialTerms?.paymentTermsDays ?? ''}
                onChange={(event) =>
                  onTermsChanged({
                    paymentTermsDays:
                      event.target.value === '' ? undefined : Number.parseInt(event.target.value, 10)
                  })
                }
                className={cn(inputClass, 'w-full')}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-ink-soft">
              {t('form.defaultRate')}
              <input
                type="number"
                min={0}
                step="0.01"
                value={input.commercialTerms?.defaultRate ?? ''}
                onChange={(event) =>
                  onTermsChanged({
                    defaultRate:
                      event.target.value === '' ? undefined : Number.parseFloat(event.target.value)
                  })
                }
                className={cn(inputClass, 'w-full')}
              />
            </label>
          </div>
        </div>

        {mutationError !== null && !duplicate && (
          <p role="alert" className="mt-3 text-sm text-blocked">
            {toMessage(mutationError)}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCloseClicked}
            className="rounded-md border border-input px-3 py-1.5 text-sm text-ink-soft hover:bg-muted"
          >
            {t('form.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void onSubmitClicked()}
            disabled={submitting}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {submitting
              ? t('form.saving')
              : row !== null
                ? t('form.submitEdit')
                : t('form.submitCreate')}
          </button>
        </div>
      </div>
    </div>
  )
}
